import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Download, ArrowUpRight, ArrowRight } from 'lucide-react';
import { PRODUCTS, getSetHero, resolveSetItem } from '../data/products';
import GoldRule from '../components/GoldRule';
import { FeatureBadge } from '../components/FeatureBadge';
import ProductImage from '../components/ProductImage';
import useProducts from '../hooks/useProducts';
import useProductImages from '../hooks/useProductImages';
import BuyButton from '../components/BuyButton';
import SetFilmStrip from '../components/SetFilmStrip';
import SetEnquiryForm from '../components/SetEnquiryForm';
import LogoCustomizer from '../components/LogoCustomizer';

// A piece reads naturally with an embossed logo when it has a flat,
// prominent surface. Grouped so the customizer can offer up to two
// contrasting canvases per set (e.g. a bag AND a wallet) when both exist.
const BAG_LIKE_CATEGORIES = ['Business Bags', 'Bags', 'Portfolios'];
const FLAT_LIKE_CATEGORIES = ['Wallets', 'Travel', 'Notebooks', 'Accessories'];

export default function SetDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { products: PRODUCTS_MERGED, sets: SETS_MERGED } = useProducts();
  const { manifest } = useProductImages();
  const [confirm, setConfirm] = useState(false);
  const [designUrl, setDesignUrl] = useState(null);

  const set = SETS_MERGED.find((s) => s.slug === slug) || SETS_MERGED[0];
  const items = useMemo(() => set.items.map((entry) => {
    const resolved = resolveSetItem(entry, PRODUCTS_MERGED);
    // Use the INK-processed clean image from the manifest when available
    const cleanImgs = (manifest[resolved.code] || manifest[entry] || []).filter((u) => u.includes('-clean'));
    return {
      ...resolved,
      code: resolved.code || entry,
      image: cleanImgs[0] || null,
    };
  }), [set.items, PRODUCTS_MERGED, manifest]);

  // Offer up to two contrasting "canvas" pieces for the logo customizer —
  // a bag-like piece (logo sits below the handle juncture) and a flatter
  // piece like a wallet (logo centered on the panel) — whichever this set
  // actually contains.
  const canvasOptions = useMemo(() => {
    const withImages = items.filter((i) => i.image && !i.pending);
    if (!withImages.length) return [];
    const bag = BAG_LIKE_CATEGORIES.map((cat) => withImages.find((i) => i.category === cat)).find(Boolean);
    const flat = FLAT_LIKE_CATEGORIES.map((cat) => withImages.find((i) => i.category === cat)).find(Boolean);
    const picked = [bag, flat].filter(Boolean);
    return picked.length ? picked : [withImages[0]];
  }, [items]);

  const onEnquire = () => {
    const el = document.getElementById('enquire');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else { setConfirm(true); setTimeout(() => setConfirm(false), 4500); }
  };

  return (
    <div data-testid={`set-detail-${set.slug}`}>
      {/* TAB NAV */}
      <div className="hairline-b border-subtle sticky top-[72px] z-40 bg-mb">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 overflow-x-auto">
          <div className="flex items-center gap-8 py-5 whitespace-nowrap">
            {SETS_MERGED.map((s) => {
              const active = s.slug === set.slug;
              return (
                <button
                  key={s.slug}
                  onClick={() => navigate(`/sets/${s.slug}`)}
                  data-testid={`set-tab-${s.slug}`}
                  className={`eyebrow transition-colors ${
                    active
                      ? 'text-gold'
                      : 'text-mb-muted hover:text-mb'
                  }`}
                >
                  {s.name}
                  {s.featured && (
                    <span className="ml-2 text-gold">●</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* SPLIT HERO */}
      <section
        className={`${set.featured ? 'hairline-b border-gold' : 'hairline-b border-subtle'} bg-mb`}
      >
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12">
          {/* LEFT: product list */}
          <div
            className="md:col-span-5 hairline-r border-subtle px-6 md:px-12 py-16 md:py-24"
            data-testid="set-hero-left"
          >
            <div className="eyebrow text-mb-muted mb-8">Inside the set</div>
            <ol className="space-y-8">
              {items.map((item, i) => {
                const li = (set.price_breakdown || []).find((b) => b.code === item.code);
                return (
                  <li
                    key={`${item.code}-${i}`}
                    className="grid grid-cols-[28px_1fr] gap-4"
                    data-testid={`set-item-row-${item.code.replace(/\s/g, '-')}`}
                  >
                    <div className="font-display text-[11px] tracking-[0.24em] text-gold pt-1">
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <div>
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="font-display text-[18px] text-mb">
                          {item.name}
                        </div>
                        {li && (
                          <div
                            className={`eyebrow-sm shrink-0 ${li.free ? 'text-gold' : 'text-mb-muted'}`}
                            data-testid={`set-item-price-${item.code.replace(/\s/g, '-')}`}
                          >
                            {li.free ? 'Complimentary' : `£${Number(li.price).toFixed(2)}`}
                          </div>
                        )}
                      </div>
                      <div className="mt-1 eyebrow-sm text-mb-muted">
                        {item.pending
                          ? 'Awaiting photography'
                          : item.dimensions || item.category || '—'}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>

            {set.price_breakdown?.length > 0 && (
              <div className="mt-10 hairline-t border-subtle pt-6" data-testid="set-price-summary">
                <div className="flex items-baseline justify-between">
                  <span className="eyebrow-sm text-mb-muted">You pay</span>
                  <span className="font-display text-2xl text-gold">
                    £{Number(set.price || 0).toFixed(2)}
                  </span>
                </div>
                {set.price_breakdown.some((li) => li.free) && (
                  <div className="mt-2 flex items-baseline justify-between">
                    <span className="eyebrow-sm text-mb-muted">Complimentary gifts included</span>
                    <span className="eyebrow-sm text-mb">
                      £{set.price_breakdown.filter((li) => li.free).reduce((s, li) => s + Number(li.price || 0), 0).toFixed(2)} value
                    </span>
                  </div>
                )}
                {set.price_breakdown.some((li) => li.confirmed === false) && (
                  <p className="mt-4 eyebrow-sm text-mb-muted italic">
                    Final piece specifications subject to confirmation.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* RIGHT: set info */}
          <div
            className="md:col-span-7 px-6 md:px-12 py-16 md:py-24"
            data-testid="set-hero-right"
          >
            <div className="eyebrow text-gold">
              {set.featured ? 'The house at full measure' : 'A house set'}
            </div>
            <h1
              className="mt-10 font-display text-5xl md:text-7xl text-mb leading-[0.95]"
              data-testid="set-name"
            >
              {set.name}
            </h1>
            <div className="mt-8">
              <GoldRule size="med" />
            </div>
            <p className="mt-8 font-display italic text-[18px] md:text-[22px] text-mb-muted leading-relaxed max-w-xl">
              {set.tagline}
            </p>
            <p className="mt-6 text-[14px] text-mb-muted leading-relaxed max-w-xl">
              {set.description}
            </p>

            {/* metadata row */}
            <div className="mt-10 hairline-t hairline-b border-subtle py-5 grid grid-cols-3 gap-4">
              <div>
                <div className="eyebrow-sm text-mb-muted">Pieces</div>
                <div className="mt-2 font-display text-[18px] text-mb">
                  {set.pieceCount}
                </div>
              </div>
              <div>
                <div className="eyebrow-sm text-mb-muted">Leather</div>
                <div className="mt-2 font-display text-[14px] text-mb">
                  {set.leather}
                </div>
              </div>
              <div>
                <div className="eyebrow-sm text-mb-muted">
                  {set.laptop ? 'Laptop' : 'Presentation'}
                </div>
                <div className="mt-2 font-display text-[14px] text-mb">
                  {set.laptop || 'Presented by hand'}
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="mt-10 flex flex-wrap items-center gap-4">
              {set.price ? (
                <BuyButton
                  kind="set"
                  targetId={set.slug}
                  label="Buy the set"
                  priceLabel={`${(set.currency || 'GBP').toUpperCase()} ${Number(set.price).toFixed(2)}`}
                  testid="set-buy-btn"
                  designUrl={designUrl}
                />
              ) : null}
              <button
                className={set.price ? 'btn-outline-gold' : 'btn-gold'}
                onClick={onEnquire}
                data-testid="set-enquire-btn"
              >
                Enquire
                <ArrowUpRight size={14} strokeWidth={1.25} />
              </button>
              <button
                className="btn-outline-gold"
                onClick={onEnquire}
                data-testid="set-download-btn"
              >
                <Download size={14} strokeWidth={1.25} />
                Download set sheet
              </button>
            </div>

            {confirm && (
              <div
                className="mt-8 hairline border-gold p-5 bg-mb-surface"
                data-testid="set-confirm-msg"
              >
                <div className="eyebrow text-gold mb-2">Noted.</div>
                <p className="text-[14px] text-mb-muted">
                  Your enquiry has been received. An atelier representative
                  will be in touch within one business day.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* PRODUCT DETAIL STRIP */}
      <section data-testid="set-detail-strip">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
          {/* Set hero composition */}
          <div className="mb-16">
            <ProductImage
              code={null}
              label={set.name}
              src={getSetHero(set.slug)}
              ratio="aspect-[16/9]"
            />
          </div>

          <div className="flex items-end justify-between mb-14">
            <div>
              <div className="eyebrow text-mb-muted mb-6">The pieces</div>
              <h2 className="font-display text-4xl md:text-5xl text-mb">
                Each, on its own terms.
              </h2>
              <p className="hidden md:block mt-6 font-display italic text-[16px] text-mb-muted">
                Scroll on — the strip advances one piece at a time.
              </p>
            </div>
            <div className="hidden md:block eyebrow-sm text-mb-muted">
              {items.length} pieces
            </div>
          </div>

          <div className="md:hidden grid gap-8">
            {items.map((item, i) => (
              <article
                key={`${item.code}-detail-${i}`}
                className="bg-mb-surface hairline border-subtle p-6 md:p-8"
                data-testid={`set-item-detail-${item.code.replace(/\s/g, '-')}`}
              >
                <ProductImage code={item.code} label={item.name} src={item.image} ratio="aspect-[4/3]" />

                <div className="mt-6 flex items-start justify-between gap-4">
                  <div>
                    <div className="eyebrow-sm text-gold">{item.category}</div>
                    <h3 className="mt-3 font-display text-[24px] text-mb leading-tight">
                      {item.name}
                    </h3>
                  </div>
                  <div className="eyebrow-sm text-mb-muted whitespace-nowrap">
                    {item.category}
                  </div>
                </div>

                <p className="mt-4 text-[13px] text-mb-muted leading-relaxed">
                  {item.description}
                </p>
                {item.code && !item.pending && (
                  <p className="mt-2 text-[11px] tracking-widest text-mb-muted/50 uppercase select-all">
                    Product code: {item.code}
                  </p>
                )}

                <div className="mt-6 hairline-t border-subtle pt-5 grid grid-cols-2 gap-4">
                  <div>
                    <div className="eyebrow-sm text-mb-muted">Dimensions</div>
                    <div className="mt-2 font-display text-[14px] text-mb">
                      {item.dimensions || '—'}
                    </div>
                  </div>
                  <div>
                    <div className="eyebrow-sm text-mb-muted">Leather</div>
                    <div className="mt-2 font-display text-[14px] text-mb">
                      {item.leather || '—'}
                    </div>
                  </div>
                </div>

                {item.features?.length > 0 && (
                  <div className="mt-6 flex flex-wrap gap-2">
                    {item.features.map((f, idx) => (
                      <FeatureBadge key={`${item.code}-f-${idx}`}>
                        {f}
                      </FeatureBadge>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FILM STRIP — scroll sequence through every piece (desktop) */}
      <SetFilmStrip set={set} items={items} />

      {/* MAKE IT YOURS — logo customizer preview */}
      <LogoCustomizer canvasOptions={canvasOptions} onDesignUploaded={setDesignUrl} />

      {/* ENQUIRE — replaces the old standalone corporate page */}
      <SetEnquiryForm setName={set.name} />

      {/* NEXT SET */}
      <NextSet current={set.slug} />
    </div>
  );
}

const NextSet = ({ current }) => {
  const { sets: SETS_MERGED } = useProducts();
  const idx = SETS_MERGED.findIndex((s) => s.slug === current);
  const next = SETS_MERGED[(idx + 1) % SETS_MERGED.length];
  return (
    <section
      className="hairline-t border-subtle"
      data-testid="next-set-strip"
    >
      <Link
        to={`/sets/${next.slug}`}
        className="block max-w-[1400px] mx-auto px-6 md:px-12 py-14 group hover:bg-mb-surface transition-colors"
      >
        <div className="flex items-center justify-between gap-6">
          <div>
            <div className="eyebrow-sm text-mb-muted">Next set</div>
            <div className="mt-3 font-display text-3xl md:text-5xl text-mb group-hover:text-gold transition-colors">
              {next.name}
            </div>
          </div>
          <div className="eyebrow text-gold">
            <ArrowRight size={18} strokeWidth={1.25} />
          </div>
        </div>
      </Link>
    </section>
  );
};
